import { computed, ref, watch } from 'vue';

/**
 * Хук синхронизации записи данных родительской и дочекрних форм.
 *
 * Родительская форма выполнит onFinish только если дочерние формы выполнили onFinish успешно.
 *
 * Все дочерние компоненты должны иметь функцию onFinish, которая отвечает за передачу данных в бэкэнд.
 * Эта функция должна быть определена в defineExpose компоненты.
 */
export function useFormSyncSubmit() {
  const parentCallback = ref(() => {});
  const children = ref([]);

  /**
   * @param {function} callback функция, вызываемая для сохранения (submit) родителькой формы.
   */
  const registerParentCallback = (callback) => {
    if (callback) parentCallback.value = callback;
  };

  /**
   * Регистрирует дочернюю компоненту.
   * @param childConfig
   * @param {string}    childConfig.id  Идентификатор формы
   * @param {Ref} childConfig.ref  Ссылка на дочернюю компоненту.
   * @param {bool} childConfig.enabled  Флаг
   */
  const registerChild = (childConfig) => {
    if (childConfig) {
      children.value.push({
        id: childConfig.id,
        ref: childConfig.ref,
        enabled: childConfig.enabled,
        saved: false,
      });
    }
  };

  /**
   * Выполняет сохранение данных дочерних форм или родительсокй формы, если дочерние формы не зарегистрированы.
   */
  const submit = async () => {
    resetSaveResults();

    for (const child of children.value) {
      if (child.ref && child.enabled) {
        await child.ref.onFinish();
      } else {
        child.saved = true;
      }
    }
  };

  /**
   * Устанавливает флаг, что сохранение данных дочерней компоненты произошло успешно.
   * @param {string} childId  Идентификатор формы
   */
  const childSaved = (childId) => {
    const child = children.value.find((child) => child.id === childId);
    if (child === null)
      throw new Error(`Component with id=${child} is not registered.`);
    child.saved = true;
  };

  const allChildrenSaved = computed(() => {
    return (
      children.value.length === 0 ||
      children.value.every((el) => el.saved === true)
    );
  });

  /**
   * Сбрасывает в false результаты сохранения для дочерних компонент.
   */
  const resetSaveResults = () => {
    children.value.forEach((el) => {
      el.saved = false;
    });
  };

  watch(allChildrenSaved, async (newVal) => {
    if (newVal) {
      await parentCallback.value();
    }
  });

  return {
    registerParentCallback,
    registerChild,
    submit,
    childSaved,
    allChildrenSaved,
    resetSaveResults,
  };
}
